import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Navigate, useNavigate } from 'react-router-dom';
import StyledComponent from 'styled-components';

import ApiRequest from '../../api/request';
import { ApiManager } from '../../managers';
import { CookieValues } from '../../types';
import { AlertContext } from '../Alert';
import { FormValidatedRef } from '../Form/Form.types';
import FormLoader from '../Form/FormLoader';
import FormValidated from '../Form/FormValidated';

const Container = StyledComponent.div`
			margin-top: 100px;
			margin-bottom: 100px;

      form {
        width: 420px;
      }
    `;

/**
	Handles displaying a page model and user interactions
  State:
  @param 	{bool}		isLoading		If loading is occuring on the page or not
  */
function PageController({ pageComponents }: { pageComponents: any[] }) {
  const navigate = useNavigate();

  const { showAlert } = useContext(AlertContext);

  const [cookies, setCookies] = useCookies<'token', CookieValues>(['token']);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const formRef = useRef<FormValidatedRef>(null);

  // MARK: - Form related
  const formOnSubmit = useCallback(async (evt: any, buttonData: any) => {
    try {
      evt.preventDefault();

      // Validations
      if (isLoading) {
        return false;
      }

      setIsLoading(true);

      // Find form component
      let formComponent = null;
      for (let i = 0; i < pageComponents.length; i++) {
        if (pageComponents[i].type === 'form') {
          formComponent = pageComponents[i];
          break;
        }
      }

      // Get params
      const params: any = {};
      if (formComponent !== null) {
        for (let i = 0; i < formComponent.form.length; i++) {
          if (!['button', 'space', 'link', '_form_footer_'].includes(formComponent.form[i].type)) {
            console.log('Calling');
            // @ts-ignore
            params[formComponent.form[i].id] = formRef.current?.getValueFor(
              formComponent.form[i].id,
            );
          }
        }
      }

      const response = await ApiRequest.sendRequest({
        method: 'post',
        params,
        path: buttonData.route,
      });
      // Success
      if (response.data.error === null) {
        // Handle success actions
        for (let i = 0; i < buttonData.successActions.length; i++) {
          switch (buttonData.successActions[i].action) {
            case 'alert':
              showAlert({
                headerText: buttonData.successActions[i].title,
                detailText: response.data[buttonData.successActions[i].messageDataField],
                variant: buttonData.successActions[i].variant,
                dismissText: buttonData.successActions[i].dismissText,
              });
              break;
            case 'setCookie':
              console.log('PageController.setCookie');
              setCookies(
                buttonData.successActions[i].cookieField,
                response.data[buttonData.successActions[i].dataField],
              );
              if (buttonData.successActions[i].cookieField === 'token') {
                // If changing API token we need to update API manager
                ApiManager.Init(cookies.token);
              } else if (buttonData.successActions[i].cookieField === 'user') {
                navigate('/home');
              }
              break;
            case 'setState':
              console.log('Update this');
              // this.props.updateMasterState({ [buttonData.successActions[i].id]: buttonData.successActions[i].dataField === '' ? buttonData.successActions[i].data : response.data[buttonData.successActions[i].dataField] });
              break;
            default:
              break;
          }
        }
      }
      // Failed
      else {
        showAlert({
          headerText: 'Uh-oh',
          detailText: response.data.error,
          variant: 'danger',
          dismissText: 'okay',
        });
      }
    } catch (err: any) {
      console.log(err);
      showAlert({
        headerText: 'Uh-oh',
        detailText: `An error has occurred, please try again or contact support.\nError: ${err.emssage}`,
        variant: 'danger',
        dismissText: 'okay',
        err,
      });
    } finally {
      setIsLoading(false);
    }
  }, [showAlert, navigate, setCookies, cookies.token, isLoading, pageComponents]);

  const resetComponents = useCallback(() => {
    // @ts-ignore
    formRef.current?.updateAllFormInputValues();
  }, []);

  const formComponent = useMemo(() => {
    let formComponent = null;
    for (let i = 0; i < pageComponents.length; i++) {
      if (pageComponents[i].type === 'form') {
        formComponent = pageComponents[i];
        break;
      }
    }
    if (formComponent !== null) {
      for (let i = 0; i < formComponent.form.length; i++) {
        // Hijack onclick handler for buttons
        if (formComponent.form[i].type === 'button') {
          formComponent.form[i].onClick = formOnSubmit;
        }
        // Reset components when changing page
        else if (formComponent.form[i].type === 'link') {
          formComponent.form[i].onClick = resetComponents;
        }
      }
    }
    return formComponent?.form;
  }, [pageComponents, formOnSubmit, resetComponents]);

  if (cookies.token && cookies.user?.authorization) {
    return <Navigate to="/home" replace />;
  }

  return (
    <Container>
      {pageComponents.map((component, i) => {
        // console.log(component);
        switch (component.type) {
          case 'form':
            return (
              <div key={`component-${i}-row`}>
                <FormLoader isLoading={isLoading} key={`component-${i}-loader`} />
                <FormValidated
                  ref={formRef}
                  formClassName="form-width"
                  formInputs={formComponent}
                  formOnChange={() => {}}
                  validateOnInit={false}
                  columns={-1}
                  dynamicInputs
                  key={`component-${i}-form`}
                  description=""
                />
              </div>
            );
          default:
            return <div key={`component-${i}-div`} />;
        }
      })}
    </Container>
  );
}

export default PageController;
